import type { ReactNode } from "react";
import { cn } from "../../app/components/primitives/utils";

// Inline-SVG product illustrations: we have no product photography, so each
// product family is drawn as a simple, tasteful render in the theme's own
// colours. Paper is always white (it is paper, in light or dark), sitting on
// a token-coloured backdrop. Black-only configurations render greyscale.

export type ProductRenderKind =
  | "letterhead"
  | "business-card"
  | "leaflet"
  | "compliment-slip"
  | "ncr-pad"
  | "appointment-card"
  | "signage"
  | "sheet";

export type ProductRenderView = "front" | "detail" | "in-use";

/** Which illustration fits a catalogue item, from its own name and family. */
export function renderKindFor(item: {
  name: string;
  product_family: string;
}): ProductRenderKind {
  const text = `${item.name} ${item.product_family}`.toLowerCase();
  if (text.includes("letterhead")) return "letterhead";
  if (text.includes("business card")) return "business-card";
  if (text.includes("leaflet")) return "leaflet";
  if (text.includes("compliment")) return "compliment-slip";
  if (text.includes("ncr")) return "ncr-pad";
  if (text.includes("appointment")) return "appointment-card";
  if (text.includes("large format") || text.includes("signage"))
    return "signage";
  return "sheet";
}

const PAPER = "#ffffff";
const INK = "#2b2d38";
const FAINT = "#d9dbe3";
const ACCENT = "var(--primary)";

const SHADOW = { filter: "drop-shadow(0 6px 8px rgba(0, 0, 0, 0.2))" };

/** A mock text line: a thin rounded bar. */
function Line({
  x,
  y,
  w,
  h = 4,
  fill = FAINT,
}: {
  x: number;
  y: number;
  w: number;
  h?: number;
  fill?: string;
}) {
  return <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={fill} />;
}

function LogoMark({ x, y, r = 8 }: { x: number; y: number; r?: number }) {
  return (
    <g>
      <circle cx={x} cy={y} r={r} fill={ACCENT} />
      <circle cx={x + r * 0.45} cy={y - r * 0.2} r={r * 0.5} fill={PAPER} />
    </g>
  );
}

function LetterheadArt() {
  return (
    <g style={SHADOW}>
      <rect x="104" y="14" width="192" height="272" rx="2" fill={PAPER} />
      <LogoMark x={128} y={44} />
      <Line x={142} y={38} w={64} h={5} fill={INK} />
      <Line x={142} y={47} w={40} h={3} />
      <Line x={236} y={38} w={44} h={3} />
      <Line x={246} y={45} w={34} h={3} />
      <Line x={250} y={52} w={30} h={3} />
      <rect x="120" y="66" width="160" height="2" fill={ACCENT} />
      <Line x={120} y={92} w={60} h={3} fill={INK} />
      <Line x={120} y={99} w={46} h={3} />
      {Array.from({ length: 12 }).map((_, i) => (
        <Line
          key={i}
          x={120}
          y={128 + i * 10}
          w={i % 4 === 3 ? 90 : 160}
          h={3}
        />
      ))}
      <Line x={120} y={262} w={160} h={2} />
      <Line x={160} y={270} w={80} h={3} />
    </g>
  );
}

function CardFace({
  x,
  y,
  variant,
}: {
  x: number;
  y: number;
  variant: "front" | "back";
}) {
  const w = 150;
  const h = 94;
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect
        width={w}
        height={h}
        rx="4"
        fill={variant === "front" ? PAPER : ACCENT}
      />
      {variant === "front" ? (
        <>
          <LogoMark x={20} y={22} r={7} />
          <Line x={34} y={17} w={50} h={5} fill={INK} />
          <Line x={34} y={25} w={32} h={3} />
          <Line x={14} y={56} w={62} h={3} fill={INK} />
          <Line x={14} y={64} w={80} h={3} />
          <Line x={14} y={72} w={70} h={3} />
          <Line x={14} y={80} w={56} h={3} />
        </>
      ) : (
        <>
          <circle cx={w / 2} cy={h / 2} r="16" fill={PAPER} opacity="0.95" />
          <circle cx={w / 2 + 7} cy={h / 2 - 3} r="8" fill={ACCENT} />
        </>
      )}
    </g>
  );
}

function BusinessCardsArt({ glossy }: { glossy: boolean }) {
  return (
    <g>
      <g style={SHADOW} transform="rotate(-14 200 160)">
        <CardFace x={125} y={113} variant="back" />
      </g>
      <g style={SHADOW} transform="rotate(10 200 160)">
        <CardFace x={125} y={113} variant="back" />
      </g>
      <g style={SHADOW}>
        <CardFace x={125} y={113} variant="front" />
        {glossy && (
          <path
            d="M156 113 L184 113 L125 190 L125 156 Z"
            fill="#8f95ad"
            opacity="0.16"
          />
        )}
      </g>
    </g>
  );
}

function LeafletArt() {
  return (
    <g>
      <g style={SHADOW} transform="rotate(7 220 150)">
        <rect x="150" y="38" width="150" height="212" rx="2" fill={ACCENT} />
        <circle cx="225" cy="144" r="26" fill={PAPER} opacity="0.95" />
        <circle cx="234" cy="138" r="13" fill={ACCENT} />
      </g>
      <g style={SHADOW}>
        <rect x="104" y="46" width="150" height="212" rx="2" fill={PAPER} />
        <rect x="104" y="46" width="150" height="62" rx="2" fill={ACCENT} />
        <Line x={118} y={64} w={100} h={8} fill={PAPER} />
        <Line x={118} y={80} w={70} h={8} fill={PAPER} />
        <rect x="118" y="120" width="122" height="62" rx="3" fill={FAINT} />
        <path d="M118 182 l32-34 22 22 16-14 52 26 z" fill="#b5b9c8" />
        <circle cx="222" cy="136" r="7" fill={PAPER} />
        {Array.from({ length: 4 }).map((_, i) => (
          <Line key={i} x={118} y={198 + i * 10} w={i === 3 ? 60 : 122} h={3} />
        ))}
      </g>
    </g>
  );
}

function ComplimentSlipArt() {
  const slip = (
    <g>
      <rect x="70" y="80" width="260" height="124" rx="2" fill={PAPER} />
      <rect x="70" y="80" width="8" height="124" fill={ACCENT} />
      <LogoMark x={102} y={106} r={9} />
      <Line x={118} y={100} w={70} h={5} fill={INK} />
      <Line x={118} y={109} w={44} h={3} />
      <text
        x="96"
        y="158"
        fontSize="19"
        fontStyle="italic"
        fontFamily="Georgia, 'Times New Roman', serif"
        fill={INK}
      >
        With compliments
      </text>
      <Line x={96} y={176} w={110} h={2} />
      <Line x={252} y={176} w={60} h={3} />
      <Line x={262} y={184} w={50} h={3} />
    </g>
  );
  return (
    <g>
      <g
        style={SHADOW}
        transform="rotate(-4 200 150) translate(6 -22)"
        opacity="0.9"
      >
        <rect x="70" y="80" width="260" height="124" rx="2" fill="#eceef4" />
      </g>
      <g style={SHADOW} transform="translate(0 14)">
        {slip}
      </g>
    </g>
  );
}

function NcrPadArt() {
  return (
    <g style={SHADOW}>
      <rect x="112" y="40" width="184" height="244" rx="2" fill="#7cc4ec" />
      <rect x="108" y="36" width="184" height="244" rx="2" fill="#f4e27a" />
      <rect x="104" y="32" width="184" height="244" rx="2" fill="#f1a1c0" />
      <rect x="100" y="28" width="184" height="244" rx="2" fill={PAPER} />
      <rect x="100" y="28" width="184" height="22" rx="2" fill={INK} />
      <rect x="100" y="44" width="184" height="6" fill="#000" opacity="0.25" />
      <LogoMark x={118} y={72} r={7} />
      <Line x={132} y={66} w={60} h={5} fill={INK} />
      <rect
        x="224"
        y="62"
        width="48"
        height="18"
        rx="2"
        fill="none"
        stroke={ACCENT}
        strokeWidth="1.5"
      />
      <Line x={114} y={104} w={156} h={2} fill={INK} />
      {Array.from({ length: 9 }).map((_, i) => (
        <g key={i}>
          <Line x={114} y={126 + i * 16} w={44} h={3} fill={INK} />
          <rect
            x="166"
            y={133 + i * 16}
            width="104"
            height="1.5"
            fill={FAINT}
          />
        </g>
      ))}
    </g>
  );
}

function AppointmentCardArt() {
  const w = 190;
  const h = 118;
  return (
    <g>
      <g style={SHADOW} transform="rotate(-8 200 150)">
        <rect x="105" y="92" width={w} height={h} rx="5" fill={ACCENT} />
        <circle cx="200" cy="151" r="19" fill={PAPER} opacity="0.95" />
        <circle cx="208" cy="146" r="9" fill={ACCENT} />
      </g>
      <g style={SHADOW} transform="rotate(3 200 150) translate(0 -2)">
        <rect x="105" y="92" width={w} height={h} rx="5" fill={PAPER} />
        <rect x="105" y="92" width={w} height="26" rx="5" fill={ACCENT} />
        <Line x={119} y={102} w={90} h={6} fill={PAPER} />
        <Line x={119} y={136} w={38} h={4} fill={INK} />
        <rect x="164" y="146" width="112" height="1.5" fill={INK} />
        <Line x={119} y={162} w={38} h={4} fill={INK} />
        <rect x="164" y="172" width="112" height="1.5" fill={INK} />
        <Line x={119} y={192} w={80} h={3} />
      </g>
    </g>
  );
}

function SignageArt() {
  return (
    <g>
      <rect x="130" y="196" width="12" height="90" fill="#8a8f9e" />
      <rect x="258" y="196" width="12" height="90" fill="#8a8f9e" />
      <g style={SHADOW}>
        <rect x="64" y="34" width="272" height="170" rx="4" fill={PAPER} />
        <rect x="64" y="34" width="272" height="64" rx="4" fill={ACCENT} />
        <Line x={90} y={56} w={150} h={12} fill={PAPER} />
        <Line x={90} y={76} w={96} h={8} fill={PAPER} />
        <Line x={90} y={122} w={200} h={6} fill={INK} />
        <Line x={90} y={138} w={170} h={6} />
        <Line x={90} y={154} w={186} h={6} />
        <LogoMark x={296} y={170} r={14} />
      </g>
    </g>
  );
}

function SheetArt() {
  return (
    <g style={SHADOW}>
      <rect x="104" y="30" width="192" height="240" rx="2" fill={PAPER} />
      <LogoMark x={128} y={58} />
      <Line x={142} y={52} w={70} h={5} fill={INK} />
      {Array.from({ length: 10 }).map((_, i) => (
        <Line
          key={i}
          x={120}
          y={100 + i * 14}
          w={i % 3 === 2 ? 90 : 160}
          h={3}
        />
      ))}
    </g>
  );
}

function Artwork({
  kind,
  glossy,
}: {
  kind: ProductRenderKind;
  glossy: boolean;
}) {
  switch (kind) {
    case "letterhead":
      return <LetterheadArt />;
    case "business-card":
      return <BusinessCardsArt glossy={glossy} />;
    case "leaflet":
      return <LeafletArt />;
    case "compliment-slip":
      return <ComplimentSlipArt />;
    case "ncr-pad":
      return <NcrPadArt />;
    case "appointment-card":
      return <AppointmentCardArt />;
    case "signage":
      return <SignageArt />;
    case "sheet":
      return <SheetArt />;
  }
}

// Close-up crop of each illustration (always 4:3, like the full view).
const DETAIL_VIEWBOX: Record<ProductRenderKind, string> = {
  letterhead: "100 20 200 150",
  "business-card": "110 90 180 135",
  leaflet: "96 36 180 135",
  "compliment-slip": "70 100 200 150",
  "ncr-pad": "96 22 200 150",
  "appointment-card": "100 90 200 150",
  signage: "70 30 220 165",
  sheet: "100 24 200 150",
};

/** A flat-lay scene: the product on a desk with a pen and a mug. */
function InUse({ children }: { children: ReactNode }) {
  return (
    <g>
      <rect width="400" height="300" fill="var(--accent)" />
      <rect y="0" width="400" height="300" fill="#000" opacity="0.04" />
      <g transform="translate(200 150) rotate(-5) scale(0.82) translate(-200 -150)">
        {children}
      </g>
      <g transform="translate(330 214) rotate(-28)">
        <rect width="86" height="7" rx="3.5" fill={INK} />
        <rect x="70" width="16" height="7" rx="3.5" fill={ACCENT} />
        <path d="M0 3.5 l-9 0 l3 -2 z" fill={INK} />
      </g>
      <g transform="translate(40 236)">
        <ellipse cx="0" cy="34" rx="30" ry="6" fill="#000" opacity="0.15" />
        <circle cx="0" cy="0" r="26" fill={PAPER} />
        <circle cx="0" cy="0" r="19" fill="#7a4b32" />
        <path
          d="M24 -8 q18 0 18 14 q0 14 -18 14"
          fill="none"
          stroke={PAPER}
          strokeWidth="6"
        />
      </g>
    </g>
  );
}

export function ProductRender({
  kind,
  view = "front",
  monochrome = false,
  glossy = false,
  className,
  title,
}: {
  kind: ProductRenderKind;
  view?: ProductRenderView;
  /** Black-only print: the whole illustration renders in greyscale. */
  monochrome?: boolean;
  /** A gloss finish adds a sheen to the cards. */
  glossy?: boolean;
  className?: string;
  /** Accessible description; omit for a purely decorative render. */
  title?: string;
}) {
  const art = <Artwork kind={kind} glossy={glossy} />;
  const viewBox = view === "detail" ? DETAIL_VIEWBOX[kind] : "0 0 400 300";
  return (
    <svg
      viewBox={viewBox}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className={cn("block size-full", monochrome && "grayscale", className)}
    >
      {view === "in-use" ? <InUse>{art}</InUse> : art}
    </svg>
  );
}
